import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Button from "../../../components/Button/Button";
import { format, addMonths } from "date-fns";
import { FaCalendarAlt, FaEuroSign, FaInfoCircle, FaCheck, FaTimes } from "react-icons/fa";
import {
  updateReglement,
  createAchatsFractionnes,
} from "../../../services/achatsServices";
import { sendNotification } from "../../../services/notificationServices";

/* ---------- Utils ---------- */
function formatEUR(n) {
  const num = Number(n) || 0;
  return num.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });
}

/** Construit un échéancier exact en centimes (la dernière échéance absorbe le reliquat). */
function buildInstallments(totalAmount, count, paymentInterval) {
  const totalCents = Math.round(Number(totalAmount) * 100);
  const base = Math.floor(totalCents / count);
  const remainder = totalCents - base * count;

  const today = new Date();
  const out = [];

  for (let i = 0; i < count; i++) {
    const cents = i === count - 1 ? base + remainder : base;
    const amount = cents / 100;
    const dueDate = addMonths(today, i * (Number(paymentInterval) || 1));
    out.push({ date: dueDate, amount });
  }
  return out;
}

/* ---------- Component ---------- */
const PaymentPlanModal = ({
  isOpen,
  onClose,
  totalAmount,
  maxPayments,
  paymentInterval,
  handlePaymentPlanSelection,
  article,
  refreshDataAfterUpdate,
}) => {
  const [selectedPlan, setSelectedPlan] = useState(null); // ex: 3
  const [installments, setInstallments] = useState([]);   // [{date, amount}]
  const [token, setToken] = useState("");
  const [confirming, setConfirming] = useState(false);

  // Bloque le scroll de fond quand la modale est ouverte
  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    setToken(storedToken || "");
  }, []);

  // Recalcule l’échéancier quand le plan change
  useEffect(() => {
    if (!selectedPlan) {
      setInstallments([]);
      return;
    }
    setInstallments(
      buildInstallments(Number(totalAmount), selectedPlan, Number(paymentInterval))
    );
  }, [selectedPlan, totalAmount, paymentInterval]);

  // Options de plans: x2 → xN
  const planOptions = useMemo(() => {
    const m = Math.max(Number(maxPayments) || 2, 2);
    return Array.from({ length: m - 1 }, (_, i) => i + 2);
  }, [maxPayments]);

  const onChoosePlan = (plan) => {
    setSelectedPlan(plan);
    refreshDataAfterUpdate?.();
  };

  const handleConfirm = async () => {
    if (!selectedPlan || !article || installments.length === 0) return;

    try {
      setConfirming(true);

      // 1) Mise à jour de l’achat initial => 1ère échéance
      const first = installments[0];
      const updatedAchat = {
        user_achat_id: article.user_achat_id,
        article_id: article.article_id, // peut être null
        quantite: article.quantite,
        prix_total: Number(first.amount),
        date_achat: article.date_achat,
        message_achat: `Échéance 1/${selectedPlan} : Règlement Licence-Cotisation`,
        statut_reglement: article.statut_reglement,
        type_reglement: article.type_reglement,
        type_achat: article.type_achat,
      };
      await updateReglement(article.id, updatedAchat);

      // 2) Création des achats fractionnés restants
      const createData = {
        user_achat_id: article.user_achat_id,
        original_achat_id: article.id,
        totalAmount: Number(totalAmount),
        paymentPlan: selectedPlan,
        paymentInterval: Number(paymentInterval),
      };
      await createAchatsFractionnes(createData);

      // 3) Notifications pour les échéances futures
      for (let i = 1; i < installments.length; i++) {
        const inst = installments[i];
        const message = `Merci de penser à régler l'échéance n°${i + 1} (${formatEUR(
          inst.amount
        )}) prévue le ${format(inst.date, "dd/MM/yyyy")}.`;
        await sendNotification(message, article.user_achat_id, token);
      }

      refreshDataAfterUpdate?.();
      handlePaymentPlanSelection?.(selectedPlan);
      onClose?.();
    } catch (error) {
      console.error("Erreur lors du fractionnement :", error);
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  // --- Rendu plein écran via portal (sans utiliser Modal.jsx) ---
  return createPortal(
    <div className="fixed inset-0 z-[1100]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      {/* Feuille centrée responsive */}
      <div className="absolute inset-0 flex items-end sm:items-center justify-center p-2 sm:p-4">
        <div
          className="
            relative w-full sm:max-w-2xl
            max-h-[92svh] sm:max-h-[88svh]
            bg-white dark:bg-slate-900
            rounded-2xl shadow-2xl
            ring-1 ring-slate-200/70 dark:ring-slate-700/70
            flex flex-col overflow-hidden
          "
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          {/* Header sticky */}
          <div className="sticky top-0 z-10 border-b border-slate-200/70 dark:border-slate-700/70 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white">
                Règlement en plusieurs fois
              </h3>
              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-900 dark:text-slate-100"
                aria-label="Fermer"
                title="Fermer"
              >
                <FaTimes />
                <span className="hidden sm:inline">Fermer</span>
              </button>
            </div>
          </div>

          {/* Corps scrollable (vertical + horizontal si besoin) */}
          <div className="flex-1 overflow-y-auto overscroll-contain overflow-x-auto px-4 sm:px-6 py-4 pb-28">
            {/* Bandeau total */}
            <div className="rounded-xl bg-slate-50 dark:bg-slate-800 ring-1 ring-slate-200 dark:ring-slate-700 p-4 mb-4 flex items-center justify-between min-w-[320px]">
              <div>
                <p className="text-sm text-slate-600 dark:text-slate-300">Montant total</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">
                  {formatEUR(totalAmount)}
                </p>
              </div>
              <div className="text-slate-500 dark:text-slate-300 flex items-center gap-2">
                <FaEuroSign />
                <span className="text-sm">Paiement sécurisé</span>
              </div>
            </div>

            {/* Choix du plan */}
            <div className="mb-4 min-w-[320px]">
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100 mb-2">
                Choisissez le nombre d’échéances
              </p>
              <div className="flex flex-wrap gap-2">
                {planOptions.map((p) => {
                  const active = selectedPlan === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => onChoosePlan(p)}
                      className={[
                        "inline-flex items-center justify-center h-10 px-4 rounded-xl ring-1 transition",
                        active
                          ? "bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 text-white ring-transparent shadow"
                          : "bg-white/80 dark:bg-slate-900/70 text-slate-800 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800",
                      ].join(" ")}
                      title={`Payer en ${p} fois`}
                    >
                      {`x${p}`}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                Intervalle entre échéances : {paymentInterval} mois.
              </p>
            </div>

            {/* Aperçu échéancier */}
            {installments.length > 0 && (
              <div className="mt-4 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 min-w-[320px]">
                <div className="bg-slate-100/70 dark:bg-slate-800/70 px-4 py-3">
                  <p className="text-sm font-semibold text-slate-900 dark:text-white">
                    Échéancier ({selectedPlan} fois)
                  </p>
                </div>

                {/* overflow-x-auto pour petits iPhone / zoom / paysage */}
                <div className="divide-y divide-slate-200 dark:divide-slate-700 bg-white dark:bg-slate-900 overflow-x-auto">
                  {installments.map((inst, i) => (
                    <div key={i} className="min-w-[280px] flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div
                          className={[
                            "w-7 h-7 grid place-items-center rounded-full text-xs font-semibold",
                            i === 0
                              ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200"
                              : "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
                          ].join(" ")}
                          title={i === 0 ? "Première échéance (aujourd’hui)" : undefined}
                        >
                          {i + 1}
                        </div>
                        <div className="flex flex-col">
                          <span className="text-sm text-slate-800 dark:text-slate-100 flex items-center gap-2">
                            <FaCalendarAlt className="opacity-80" />
                            {format(inst.date, "dd/MM/yyyy")}
                            {i === 0 && (
                              <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200">
                                <FaCheck /> aujourd’hui
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            Échéance {i + 1}/{selectedPlan}
                          </span>
                        </div>
                      </div>

                      <div className="text-sm sm:text-base font-semibold text-slate-900 dark:text-white">
                        {formatEUR(inst.amount)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info */}
            <div className="mt-4 flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300 min-w-[320px]">
              <FaInfoCircle className="mt-0.5 shrink-0" />
              <p>
                Le premier paiement est effectué immédiatement. Des notifications de rappel sont envoyées
                pour les échéances suivantes aux dates indiquées.
              </p>
            </div>
          </div>

          {/* Footer sticky (toujours visible) */}
          <div className="sticky bottom-0 z-10 border-t border-slate-200/70 dark:border-slate-700/70 bg-white/95 dark:bg-slate-900/95 backdrop-blur px-4 sm:px-6 py-3 sm:py-4 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
            <div className="flex items-center justify-end gap-2 mb-[50px]">
              <Button
                type="button"
                text="Annuler"
                onClick={onClose}
                className="!rounded-xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700"
              />
              <Button
                type="button"
                text={confirming ? "Traitement…" : "Confirmer"}
                onClick={handleConfirm}
                disabled={!selectedPlan || confirming}
                className={`!rounded-xl ${!selectedPlan || confirming ? "!opacity-60 cursor-not-allowed" : ""}`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PaymentPlanModal;
